import { useCallback, useEffect } from 'react';

import {
  COMPANY_LINK_MESSAGES,
  deleteCompanyLink,
  fetchCompanyLinks,
  insertCompanyLink,
  sortCompanyLinks,
  type CompanyLink,
} from '../lib/companyLinks';
import { logError } from '../lib/logger';
import { useCompanyLinksStore, type CompanyLinksState } from '../store/companyLinksStore';
import { useAuth } from './useAuth';

/** One read at a time per account, whatever the number of menus that ask. */
const inflight = new Map<string, Promise<void>>();

/** Reads the person's links into the store. Exported for the tests and for a retry. */
export function loadCompanyLinks(userId: string): Promise<void> {
  const running = inflight.get(userId);
  if (running) return running;
  const { startLoading, setLinks, setError } = useCompanyLinksStore.getState();
  startLoading(userId);
  const read = fetchCompanyLinks()
    .then((links) => setLinks(userId, links))
    .catch((err: unknown) => {
      logError('companies', 'my_company_links unreadable', err, { userId });
      setError(userId, COMPANY_LINK_MESSAGES.readFailed);
    })
    .finally(() => inflight.delete(userId));
  inflight.set(userId, read);
  return read;
}

/** The links on screen, changed by a write that succeeded: the same list everywhere, without reading it again. */
function applyToReadLinks(userId: string, change: (links: readonly CompanyLink[]) => CompanyLink[]): void {
  const { state, setLinks } = useCompanyLinksStore.getState();
  if (state.status === 'ready' && state.userId === userId) setLinks(userId, change(state.links));
}

const NOT_READ: CompanyLinksState = { status: 'idle' };

export interface MyCompanyLinks {
  readonly state: CompanyLinksState;
  readonly retry: () => void;
  /** Writes a link (label and url already normalized); null when written, else why not, in French. */
  readonly add: (label: string, url: string) => Promise<string | null>;
  /** Removes a link; null when removed, else why not, in French. */
  readonly remove: (id: string) => Promise<string | null>;
}

/**
 * « Mes sociétés » of the signed-in person (lot BS), read once per account and shared by the menus and the profile.
 * While nothing is read for the current account, `idle` or `loading`.
 */
export function useMyCompanyLinks(): MyCompanyLinks {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const state = useCompanyLinksStore((s) => s.state);

  useEffect(() => {
    if (userId === null) {
      useCompanyLinksStore.getState().reset();
      return;
    }
    const current = useCompanyLinksStore.getState().state;
    if (current.status === 'idle' || current.userId !== userId) void loadCompanyLinks(userId);
  }, [userId]);

  const retry = useCallback(() => {
    if (userId !== null) void loadCompanyLinks(userId);
  }, [userId]);

  const add = useCallback(async (label: string, url: string): Promise<string | null> => {
    if (userId === null) return COMPANY_LINK_MESSAGES.saveFailed;
    const write = await insertCompanyLink(label, url);
    if (!write.ok) return write.message;
    applyToReadLinks(userId, (links) => sortCompanyLinks([...links, write.link]));
    return null;
  }, [userId]);

  const remove = useCallback(async (id: string): Promise<string | null> => {
    if (userId === null) return COMPANY_LINK_MESSAGES.removeFailed;
    try {
      await deleteCompanyLink(id);
    } catch (err) {
      logError('companies', 'my_company_links delete failed', err, { userId, linkId: id });
      return COMPANY_LINK_MESSAGES.removeFailed;
    }
    applyToReadLinks(userId, (links) => links.filter((link) => link.id !== id));
    return null;
  }, [userId]);

  // A state read for another account (the one before a sign-in) is not this one's.
  const own = state.status === 'idle' || state.userId === userId ? state : NOT_READ;
  return { state: own, retry, add, remove };
}
